import { css } from '@emotion/core';
import {
  CheckboxField,
  ChecklistField,
  Modal,
  OptionSwitchField,
  Tabs,
  Text,
  useForm,
} from '@weave/design-system';
import { theme } from '@weave/theme-original';
import { CenteredSpinningLoader } from 'components/shared';
import {
  CustomizationFlagStatesV2,
  LocationFeatureV2,
} from 'models/location-feature.model';
import { getVerticalList } from 'models/vertical-enum';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectFeatures } from 'redux/actions/customization/customization.action';
import {
  BundlePackagesEnum,
  CustomizationFlag,
  CustomizationFlagsRequest,
  IntegrationPackagesEnum,
} from '../customization-page-v2.types';
import { useCustomizationFlagV2Store } from '../providers/customization-flag-editable-provider';

import {
  bundleAddOnOptions,
  BundleAddOnsEnum,
  computeNewBundle,
  computeNewBundleAddOns,
  getBundlePackageName,
  getCustomizationFlagByBundlesRequest,
  getCustomizationFlagStateColor,
  getIntegrationPackageName,
  getProductPackageBundleName,
} from '../customization-page.utils';
import {
  useManageCountryCodeVerified,
  useManageProhibitedCustomizationFlags,
} from '../hooks';
import {
  useCustomizationBundleFlagUpdater,
  useCustomizationTableFilter,
} from '../providers';

type Tab = 'new-bundle' | 'old-bundle';

export const CustomizationFlagEditModal = () => {
  const { resetFilters } = useCustomizationTableFilter();
  const customizationFlags = useSelector(selectFeatures);
  const updateBundleCustomizationFlags = useCustomizationBundleFlagUpdater();
  const { manageProhibitedCustomizationFlags } = useManageProhibitedCustomizationFlags();
  const { manageCountryCodeVerified } = useManageCountryCodeVerified();
  const verticalList = getVerticalList();

  const [tab, setTab] = useState<Tab>('new-bundle');

  const {
    verticalId,
    customizationFlagEditModalProps,
    getCustomizationsFlagsByBundles,
    setTableData,
    toggleCustomizationFlag,
    isGetCustomizationsFlagsByBundlesLoading,
  } = useCustomizationFlagV2Store();

  const { modalProps } = customizationFlagEditModalProps;

  const { formProps, getFieldProps, seedValues, values } = useForm({
    fields: {
      integrationPackage: { type: 'optionswitch', required: true, value: '' },
      bundlePackage: {
        type: 'optionswitch',
        value: '',
        required: false,
      },
      phones: { type: 'checkbox', value: true },
      bundleAddOns: {
        type: 'checklist',
        required: false,
        value: [BundleAddOnsEnum.BASE],
      },
    },
  });

  const disableFormSubmit = useMemo(() => {
    if (isGetCustomizationsFlagsByBundlesLoading) {
      return true;
    }

    if (!values.integrationPackage) {
      return true;
    }

    return tab === 'old-bundle' && !values.bundlePackage;
  }, [
    tab,
    isGetCustomizationsFlagsByBundlesLoading,
    values.integrationPackage,
    values.bundlePackage,
  ]);

  const verticalName = verticalList.find(
    (verticalDetails) => verticalDetails.key === verticalId
  )?.value;

  const getLegacyPackageBundle = () => {
    const integrationPackage = getIntegrationPackageName(
      (values.integrationPackage ?? '') as IntegrationPackagesEnum
    );
    const bundlePackage = getBundlePackageName(
      (values.bundlePackage ?? '') as BundlePackagesEnum
    );
    const isSoftwareOnly = !values.phones;
    if (integrationPackage && bundlePackage) {
      return getProductPackageBundleName(
        integrationPackage,
        bundlePackage,
        isSoftwareOnly
      );
    }

    return '';
  };

  const updateCustomizationFlagDetails = (
    customizationFlag: LocationFeatureV2,
    flagState?: CustomizationFlagStatesV2
  ) => {
    const state = flagState ?? CustomizationFlagStatesV2.ACTIVE;
    const stateColor = getCustomizationFlagStateColor(state);
    toggleCustomizationFlag(customizationFlag, {
      key: state,
      label: state.toUpperCase(),
      color: stateColor,
      isFreeTrial: false,
    });
  };

  const getUpdatedCustomizationFlagStateToHide = (
    customizationFlag: LocationFeatureV2
  ): LocationFeatureV2 => {
    toggleCustomizationFlag(customizationFlag, {
      key: CustomizationFlagStatesV2.HIDE,
      label: 'Hide',
      color: 'error',
    });
    return {
      ...customizationFlag,
      state: CustomizationFlagStatesV2.HIDE,
    };
  };

  const getUpdatedCustomizationFlagStateToActive = (
    customizationFlag: LocationFeatureV2,
    newState: CustomizationFlagStatesV2
  ): LocationFeatureV2 => {
    updateCustomizationFlagDetails(customizationFlag, newState);
    const updatedState = newState ?? CustomizationFlagStatesV2.ACTIVE;

    return {
      ...customizationFlag,
      state: updatedState,
    };
  };

  const manageCustomizationFlagsMapping = (
    customizationFlagsByBundles: CustomizationFlag[]
  ) => {
    const tableData: LocationFeatureV2[] = customizationFlags.map((customizationFlag) => {
      const customizationFlagByBundle = customizationFlagsByBundles?.find(
        (flag) => flag.id === customizationFlag.id
      );

      if (customizationFlagByBundle?.state === CustomizationFlagStatesV2.ACTIVE) {
        return getUpdatedCustomizationFlagStateToActive(
          customizationFlag,
          CustomizationFlagStatesV2.ACTIVE
        );
      }
      return getUpdatedCustomizationFlagStateToHide(customizationFlag);
    });
    setTableData(tableData);
    modalProps.onClose();
  };

  const reset = () => {
    resetFilters();
    updateBundleCustomizationFlags([]);
  };

  const handleApplyChanges = async () => {
    let request: CustomizationFlagsRequest | null = null;

    if (tab === 'new-bundle') {
      request = getCustomizationFlagByBundlesRequest(
        computeNewBundle(
          values.integrationPackage as IntegrationPackagesEnum,
          values.phones ?? false
        ),
        verticalId,
        computeNewBundleAddOns(
          values.integrationPackage as IntegrationPackagesEnum,
          values.bundleAddOns as BundleAddOnsEnum[]
        )
      );
    } else {
      const packageBundle = getLegacyPackageBundle();
      if (packageBundle) {
        request = getCustomizationFlagByBundlesRequest(packageBundle, verticalId);
      }
    }

    if (!request) {
      return;
    }

    try {
      const customizationFlagByBundlesResponse = await getCustomizationsFlagsByBundles(
        request
      );
      reset();
      const { activeFlags, prohibitedFlags, countryCodeVerified } =
        customizationFlagByBundlesResponse;
      manageCustomizationFlagsMapping(activeFlags ?? []);
      manageProhibitedCustomizationFlags(prohibitedFlags ?? []);
      manageCountryCodeVerified(!!countryCodeVerified);
    } catch (error) {
      console.error('Error fetching customization flags by bundles', error);
    }
  };

  useEffect(() => {
    seedValues({
      ...values,
      integrationPackage: '',
      bundlePackage: '',
    });
  }, []);

  return (
    <Modal {...modalProps} maxWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>
        Edit and Update Customizations
      </Modal.Header>

      <Modal.Body css={styles.modalBody}>
        {isGetCustomizationsFlagsByBundlesLoading ? (
          <CenteredSpinningLoader />
        ) : (
          <form {...formProps} css={styles.form}>
            <Text size="small" css={{ margin: 0 }}>
              Select the bundles below and click Review Changes.
            </Text>

            <div css={styles.formField}>
              <Text weight="bold" size="large" as="label">
                Select Integration
              </Text>
              <OptionSwitchField {...getFieldProps('integrationPackage')}>
                <OptionSwitchField.Option value={IntegrationPackagesEnum.CORE}>
                  Core
                </OptionSwitchField.Option>
                <OptionSwitchField.Option value={IntegrationPackagesEnum.PLUS}>
                  Plus
                </OptionSwitchField.Option>
              </OptionSwitchField>
            </div>

            <Tabs
              initialTab="new-bundle"
              onChange={(tab) => setTab(tab as Tab)}
              controlledTab={tab}
            >
              <Tabs.Bar>
                <Tabs.Tab id="new-bundle" controls="new-bundle" css={{ flex: 1 }}>
                  New Bundles
                </Tabs.Tab>
                <Tabs.Tab id="old-bundle" controls="old-bundle" css={{ flex: 1 }}>
                  Old Bundles
                </Tabs.Tab>
              </Tabs.Bar>
              <Tabs.Panel
                id="new-bundle-panel"
                controller="new-bundle"
                css={styles.tabPanel}
              >
                <div css={styles.formField}>
                  <ChecklistField
                    {...getFieldProps('bundleAddOns')}
                    label="Select Bundles"
                    css={styles.bundleField}
                  >
                    {bundleAddOnOptions.map(({ value, label }) => (
                      <ChecklistField.Option
                        key={value}
                        name={value}
                        disabled={value === BundleAddOnsEnum.BASE}
                      >
                        {label}
                      </ChecklistField.Option>
                    ))}
                  </ChecklistField>
                </div>
              </Tabs.Panel>
              <Tabs.Panel
                id="old-bundle-panel"
                controller="old-bundle"
                css={styles.tabPanel}
              >
                <div css={styles.formField}>
                  <OptionSwitchField
                    {...getFieldProps('bundlePackage')}
                    label="Select Bundles"
                    css={styles.bundleField}
                  >
                    <OptionSwitchField.Option value={BundlePackagesEnum.ULTIMATE}>
                      Ultimate
                    </OptionSwitchField.Option>
                    <OptionSwitchField.Option value={BundlePackagesEnum.ELITE}>
                      Elite
                    </OptionSwitchField.Option>
                    <OptionSwitchField.Option value={BundlePackagesEnum.PRO}>
                      Pro
                    </OptionSwitchField.Option>
                    <OptionSwitchField.Option value={BundlePackagesEnum.ESSENTIALS}>
                      Essentials
                    </OptionSwitchField.Option>
                  </OptionSwitchField>
                </div>
              </Tabs.Panel>
            </Tabs>

            <div css={styles.formField}>
              <Text size="large" weight="bold" as="label">
                Vertical
              </Text>
              <Text size="large" css={{ margin: 0 }}>
                {verticalName}
              </Text>
            </div>
            <div css={[styles.formField, styles.horizontalField]}>
              <Text size="large" weight="bold" as="label">
                Phones
              </Text>
              <CheckboxField label="" {...getFieldProps('phones')} />
            </div>
          </form>
        )}
      </Modal.Body>
      <Modal.Actions
        primaryLabel="Review Changes"
        secondaryLabel="Cancel"
        onPrimaryClick={handleApplyChanges}
        onSecondaryClick={modalProps.onClose}
        disablePrimary={disableFormSubmit}
        disableSecondary={isGetCustomizationsFlagsByBundlesLoading}
      />
    </Modal>
  );
};

const styles = {
  modalBody: css`
    display: flex;
    flex-direction: column;
    scrollbar-gutter: stable;
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,

  tabPanel: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,

  formField: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,

  horizontalField: css`
    flex-direction: row;
    align-items: center;
  `,

  bundleField: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    > label:first-of-type {
      margin-bottom: 0;
      font-weight: ${theme.font.weight.bold};
      font-size: ${theme.font.size.large};
    }

    > div {
      margin-bottom: 0;
    }
  `,
};
